import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Mic, X, Volume2, Loader2, Car } from 'lucide-react';

const PULSE_RINGS = [0, 1, 2, 3];
const SPEECH_MULTIPLIER = 1.8;
const DROP_DURATION_MS = 1300;
const CALIBRATION_MS = 600;
const MAX_RECORD_MS = 20000;

const DrivingMode = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t } = useTranslation();

  const [phase, setPhase] = useState('idle');
  const [statusText, setStatusText] = useState('');
  const [waveAmplitude, setWaveAmplitude] = useState(0);
  const [conversationActive, setConversationActive] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioElRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const mountedRef = useRef(true);
  const dropTimerRef = useRef(null);
  const maxTimerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const convActiveRef = useRef(false);
  const convIdRef = useRef(null);
  const wakeLockRef = useRef(null);

  // ── Screen Wake Lock: keep screen on during driving mode ──
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {
          // Re-acquire on visibility change (iOS releases on tab switch)
        });
      }
    } catch (e) {
      console.warn('Wake Lock not available:', e);
    }
  };

  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      try { wakeLockRef.current.release(); } catch (e) {}
      wakeLockRef.current = null;
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    requestWakeLock();

    // Re-acquire wake lock when page becomes visible again
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && mountedRef.current) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    // Create a real DOM audio element — iOS respects these better
    const audio = document.createElement('audio');
    audio.setAttribute('playsinline', '');
    audio.setAttribute('webkit-playsinline', '');
    audio.preload = 'auto';
    document.body.appendChild(audio);
    audioElRef.current = audio;

    return () => {
      mountedRef.current = false;
      cleanupAll();
      releaseWakeLock();
      document.removeEventListener('visibilitychange', handleVisibility);
      if (audio.parentNode) audio.parentNode.removeChild(audio);
    };
  }, []);

  // Auto-start: first user tap triggers briefing
  // Don't auto-play briefing — wait for first tap to respect iOS audio policy
  useEffect(() => {
    // Show ready state
    setStatusText(t('driving.tapToSpeak') || 'Appuie pour parler à ATLAS');
  }, []);

  const cleanupAll = () => {
    const el = audioElRef.current;
    if (el) { el.pause(); el.removeAttribute('src'); el.load(); }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (dropTimerRef.current) clearTimeout(dropTimerRef.current);
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      try { audioCtxRef.current.close(); } catch (e) {}
    }
  };

  const stopRecorder = () => {
    if (dropTimerRef.current) { clearTimeout(dropTimerRef.current); dropTimerRef.current = null; }
    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  // Play audio from a real HTTP URL — iOS/CarPlay compatible
  const playAudioUrl = useCallback((url, autoListenAfter = false) => {
    return new Promise((resolve) => {
      const el = audioElRef.current;
      if (!el) { resolve(); return; }

      // Clean up old listeners
      const cleanup = () => {
        el.onended = null;
        el.onerror = null;
      };

      el.onended = () => {
        cleanup();
        if (!mountedRef.current) return resolve();
        if (autoListenAfter && convActiveRef.current) {
          setTimeout(() => { if (mountedRef.current && convActiveRef.current) startListening(); }, 400);
        } else {
          setPhase('idle'); setStatusText(t('driving.tapToSpeak') || 'Appuie pour parler');
        }
        resolve();
      };

      el.onerror = () => {
        cleanup();
        console.error('Audio playback failed for URL:', url);
        if (mountedRef.current) {
          if (autoListenAfter && convActiveRef.current) {
            setTimeout(() => { if (mountedRef.current) startListening(); }, 400);
          } else {
            setPhase('idle'); setStatusText('');
          }
        }
        resolve();
      };

      el.src = url;
      el.load();
      const playPromise = el.play();
      if (playPromise) {
        playPromise.catch((err) => {
          console.error('Play error:', err);
          cleanup();
          if (mountedRef.current) {
            if (autoListenAfter && convActiveRef.current) {
              setTimeout(() => { if (mountedRef.current) startListening(); }, 400);
            } else {
              setPhase('idle'); setStatusText('');
            }
          }
          resolve();
        });
      }
    });
  }, []);

  // === BRIEFING ===
  const startBriefing = async () => {
    setPhase('briefing');
    setStatusText(t('driving.loadingBriefing') || 'Briefing...');
    try {
      const res = await api.get('/api/voice/briefing');
      if (!mountedRef.current) return;
      setConversationActive(true); convActiveRef.current = true;
      if (res.data.audio_url) {
        setPhase('speaking');
        setStatusText(t('driving.atlasSpeak') || 'ATLAS parle...');
        await playAudioUrl(res.data.audio_url, true);
      } else {
        startListening();
      }
    } catch (err) {
      console.error('Briefing error:', err);
      if (mountedRef.current) {
        // Briefing failed — go straight to listening
        setConversationActive(true); convActiveRef.current = true;
        startListening();
      }
    }
  };

  // === LISTENING with adaptive silence ===
  const startListening = async () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      try { audioCtxRef.current.close(); } catch (e) {}
    }
    if (dropTimerRef.current) { clearTimeout(dropTimerRef.current); dropTimerRef.current = null; }
    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mountedRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = stream;

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let ambientLevel = 0;
      const calibrationSamples = [];
      const calibrationStart = Date.now();
      let calibrated = false;
      let hasSpeech = false;
      let dropStarted = false;

      const updateLevel = () => {
        if (!mountedRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setWaveAmplitude(avg / 255);

        if (!calibrated) {
          calibrationSamples.push(avg);
          if (Date.now() - calibrationStart > CALIBRATION_MS) {
            ambientLevel = calibrationSamples.reduce((a, b) => a + b, 0) / calibrationSamples.length;
            calibrated = true;
          }
        } else {
          const speechThreshold = Math.max(ambientLevel * SPEECH_MULTIPLIER, ambientLevel + 8);
          if (avg > speechThreshold) {
            hasSpeech = true; dropStarted = false;
            if (dropTimerRef.current) { clearTimeout(dropTimerRef.current); dropTimerRef.current = null; }
          } else if (hasSpeech && !dropStarted) {
            dropStarted = true;
            dropTimerRef.current = setTimeout(() => { if (mountedRef.current) stopRecorder(); }, DROP_DURATION_MS);
          }
        }
        animFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();

      maxTimerRef.current = setTimeout(() => { if (mountedRef.current) stopRecorder(); }, MAX_RECORD_MS);

      audioChunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        if (dropTimerRef.current) { clearTimeout(dropTimerRef.current); dropTimerRef.current = null; }
        if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
        setWaveAmplitude(0);
        stream.getTracks().forEach(t => t.stop());
        if (audioCtx.state !== 'closed') audioCtx.close();
        if (!mountedRef.current || audioChunksRef.current.length === 0) return;
        processVoice();
      };

      recorder.start();
      setPhase('listening');
      setStatusText(t('driving.listening') || 'Je t\'écoute...');
    } catch (err) {
      console.error('Mic error:', err);
      setStatusText(t('driving.micError') || 'Autorise le micro');
      setPhase('idle');
    }
  };

  // === PROCESS: STT → Claude → TTS (real MP3 URL) ===
  const processVoice = async () => {
    setPhase('processing');
    setStatusText(t('driving.thinking') || 'ATLAS réfléchit...');

    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'voice.webm');
      const sttRes = await api.post('/api/voice/stt', formData);
      const userText = sttRes.data.text;

      if (!userText || userText.trim().length < 2) {
        if (mountedRef.current && convActiveRef.current) {
          setTimeout(() => { if (mountedRef.current) startListening(); }, 300);
        } else { setPhase('idle'); setStatusText(''); }
        return;
      }

      const chatRes = await api.post('/api/voice/chat', {
        conversation_id: convIdRef.current,
        content: userText,
        mode: 'decision',
      });

      if (!mountedRef.current) return;
      convIdRef.current = chatRes.data.conversation_id;

      if (chatRes.data.audio_url) {
        setPhase('speaking');
        setStatusText(t('driving.atlasSpeak') || 'ATLAS parle...');
        await playAudioUrl(chatRes.data.audio_url, convActiveRef.current);
      } else if (convActiveRef.current) {
        setTimeout(() => { if (mountedRef.current) startListening(); }, 300);
      } else {
        setPhase('idle'); setStatusText('');
      }
    } catch (err) {
      console.error('Voice error:', err);
      if (mountedRef.current) {
        setStatusText(t('driving.error') || 'Erreur.');
        setTimeout(() => {
          if (mountedRef.current && convActiveRef.current) startListening();
          else if (mountedRef.current) { setPhase('idle'); setStatusText(''); }
        }, 2000);
      }
    }
  };

  // === BUTTON — first tap starts briefing (unlocks iOS audio) ===
  const handleMainButton = () => {
    if (phase === 'listening') {
      stopRecorder();
    } else if (phase === 'speaking') {
      const el = audioElRef.current;
      if (el) { el.pause(); el.removeAttribute('src'); el.load(); }
      setTimeout(() => { if (mountedRef.current) startListening(); }, 300);
    } else if (phase === 'idle') {
      // First tap — start briefing + conversation
      if (!conversationActive) {
        setConversationActive(true); convActiveRef.current = true;
        startBriefing();
      } else {
        startListening();
      }
    }
  };

  const handleExit = () => {
    convActiveRef.current = false; setConversationActive(false);
    cleanupAll(); navigate('/dashboard');
  };

  const handlePause = () => {
    convActiveRef.current = false; setConversationActive(false);
    const el = audioElRef.current;
    if (el) { el.pause(); el.removeAttribute('src'); el.load(); }
    stopRecorder();
    setPhase('idle'); setStatusText('');
  };

  const isListening = phase === 'listening';
  const isSpeaking = phase === 'speaking';
  const isProcessing = phase === 'processing' || phase === 'briefing';
  const borderColor = isListening ? 'border-red-500' : isSpeaking ? 'border-atlasCyan' : isProcessing ? 'border-atlasPurple/50' : 'border-atlasCyan/30';
  const orbScale = isListening ? 1 + waveAmplitude * 0.35 : isSpeaking ? 1.03 : 1;

  return (
    <div data-testid="driving-mode-page"
      className="fixed inset-0 bg-atlasBg flex flex-col items-center justify-between z-50 select-none overflow-hidden"
      style={{ touchAction: 'manipulation' }}>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[120px] transition-all duration-1000"
        style={{
          opacity: isListening || isSpeaking ? 0.5 : 0.15,
          background: isListening ? 'radial-gradient(circle, rgba(239,68,68,0.25), transparent 70%)'
            : isSpeaking ? 'radial-gradient(circle, rgba(0,217,255,0.25), transparent 70%)'
            : 'radial-gradient(circle, rgba(0,217,255,0.08), transparent 70%)',
        }} />

      <div className="relative w-full flex items-center justify-between px-6 pt-6 pb-2">
        <div className="flex items-center gap-2">
          <Car className="w-5 h-5 text-atlasCyan/60" />
          <span className="text-atlasCyan/60 text-xs atlas-mono tracking-widest uppercase">
            {t('driving.modeLabel') || 'Mode Vocal'}
          </span>
          {conversationActive && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse ml-1" />}
        </div>
        <div className="flex items-center gap-2">
          {conversationActive && (
            <button data-testid="driving-pause-btn" onClick={handlePause}
              className="px-3 py-1.5 text-xs text-atlasTextSecondary border border-atlasBorder rounded-full hover:bg-atlasCard/50 transition-colors">
              Pause
            </button>
          )}
          <button data-testid="driving-exit-btn" onClick={handleExit}
            className="p-3 rounded-full hover:bg-atlasCard/50 transition-colors">
            <X className="w-6 h-6 text-atlasTextSecondary" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        <p data-testid="driving-display-text"
          className={`text-center text-sm atlas-mono tracking-wide mb-10 transition-all duration-500 ${
            isListening ? 'text-red-400' : isSpeaking ? 'text-atlasCyan'
            : isProcessing ? 'text-atlasPurple animate-pulse' : 'text-atlasTextSecondary/40'
          }`}>
          {statusText || (t('driving.tapToSpeak') || 'Appuie pour parler à ATLAS')}
        </p>

        <div className="relative">
          {(isListening || isSpeaking) && PULSE_RINGS.map((i) => (
            <div key={i}
              className={`absolute inset-0 rounded-full ${isListening ? 'border border-red-500/15' : 'border border-atlasCyan/15'}`}
              style={{ animation: 'orbPulse 2.5s ease-out infinite', animationDelay: `${i * 0.5}s` }} />
          ))}
          <button data-testid="driving-main-btn" onClick={handleMainButton} disabled={isProcessing}
            className={`relative w-36 h-36 md:w-40 md:h-40 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${borderColor} ${isProcessing ? 'opacity-50' : ''}`}
            style={{
              transform: `scale(${orbScale})`,
              background: isListening ? 'radial-gradient(circle, rgba(239,68,68,0.15) 0%, transparent 70%)'
                : isSpeaking ? 'radial-gradient(circle, rgba(0,217,255,0.15) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(0,217,255,0.05) 0%, transparent 70%)',
              boxShadow: isListening ? '0 0 60px rgba(239,68,68,0.2)' : isSpeaking ? '0 0 60px rgba(0,217,255,0.2)' : '0 0 30px rgba(0,217,255,0.05)',
            }}>
            {isProcessing ? <Loader2 className="w-12 h-12 text-atlasPurple animate-spin" />
              : isListening ? <Mic className="w-12 h-12 text-red-400 animate-pulse" />
              : isSpeaking ? <Volume2 className="w-12 h-12 text-atlasCyan" />
              : <Mic className="w-12 h-12 text-atlasCyan/70" />}
          </button>
        </div>

        <p className="text-atlasTextSecondary/30 text-xs atlas-mono mt-8">
          {isListening ? (t('driving.autoSend') || 'Envoi auto au silence')
            : isSpeaking ? (t('driving.tapToSkip') || 'Appuie pour couper')
            : isProcessing ? '' : conversationActive
            ? (t('driving.conversationActive') || 'Conversation active')
            : (t('driving.tapToSpeak') || 'Appuie pour démarrer')}
        </p>
      </div>

      <div className="pb-8" />

      <style>{`
        @keyframes orbPulse {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(2.2); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default DrivingMode;
